/**
 * Dual statement (old `showDualStatementModal` App.tsx:49549-50268 and `handlePrintDualStatement` :23500-23603): two
 * accounts' statements from `GET /customers/:id/statement` merged by time, one row per currency line (ids only, R-22
 * fixed), and the reconciliation of the two ledger balances (trust = held for the customer, debt = owed by the
 * customer; display arithmetic). Print / PDF = the A4 template with the old columns and summary; WhatsApp = the free
 * link with the summary text (D-063).
 */
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { DualStatementModal, type DualReconciliation, type DualRow, type DualSide, type DualState } from "@faylasuf/ui";
import { PRINT_ROOT_CLASS, StatementA4, printDocument, type StatementA4Props } from "@faylasuf/print";
import { useDocuments } from "../../app/documents.js";
import type { CustomerAccountRow, CustomerStatementQuery } from "../../api/types.js";
import { useApp, useSession } from "../../app/context.js";
import { isSessionLost } from "../../app/errors.js";
import { keys } from "../../app/query.js";
import { useToast } from "../../app/toast.js";
import { officeDate } from "../wallets/StatementContainer.js";
import { decAdd, fmt, fmtAbs, isZero, toScaled } from "./format.js";
import { usePrintOffice } from "../../app/printOffice.js";

function net(row: CustomerAccountRow, cur: "IQD" | "USD"): string {
  const b = cur === "USD" ? row.usd.balance : row.iqd.balance;
  return row.kind === "trust" ? b : b.startsWith("-") ? b.slice(1) : isZero(b) ? "0" : `-${b}`;
}

const isZeroDec = (v: string) => toScaled(v) === 0n;

function state(v: string): DualState {
  const s = toScaled(v);
  return s > 0n ? "we_owe" : s < 0n ? "they_owe" : "balanced";
}

export function DualStatementContainer({ rows, usdHidden, themeColor, onClose }: { rows: readonly CustomerAccountRow[]; usdHidden: boolean; themeColor: string; onClose: () => void }) {
  const docs = useDocuments();
  const [words, setWords] = useState("");
  const { source, t, locale, now } = useApp();
  const { session, sessionLost } = useSession();
  const printOffice = usePrintOffice(); // [C29] F-286: identity lines in the A4 header
  const toast = useToast();
  const tz = session.office.timeZone;
  const [first, setFirst] = useState<DualSide>({ id: "", typeFilter: "debt", search: "" });
  const [second, setSecond] = useState<DualSide>({ id: "", typeFilter: "trust", search: "" });
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [printing, setPrinting] = useState(false);
  const q: CustomerStatementQuery = { ...(startDate ? { from: startDate } : {}), ...(endDate ? { to: endDate } : {}), limit: 2000 };
  const s1 = useQuery({ queryKey: keys.customerStatement(first.id, q), queryFn: () => source.customerStatement(first.id, q), enabled: Boolean(first.id && second.id) });
  const s2 = useQuery({ queryKey: keys.customerStatement(second.id, q), queryFn: () => source.customerStatement(second.id, q), enabled: Boolean(first.id && second.id) });
  if ((s1.error && isSessionLost(s1.error)) || (s2.error && isSessionLost(s2.error))) sessionLost();

  const acc1 = rows.find((r) => r.id === first.id);
  const acc2 = rows.find((r) => r.id === second.id);
  const unit = t("unit.iqd");

  const merged: DualRow[] = useMemo(() => {
    const out: { at: string; row: DualRow }[] = [];
    for (const [acc, st] of [[acc1, s1.data], [acc2, s2.data]] as const) {
      if (!acc || !st) continue;
      for (const r of st.rows) {
        for (const cur of ["IQD", "USD"] as const) {
          const amt = cur === "USD" ? r.usd : r.iqd;
          if (isZero(amt) || (cur === "USD" && usdHidden)) continue;
          const inbound = acc.kind === "trust" ? !amt.startsWith("-") : amt.startsWith("-");
          const label = `${fmtAbs(amt)} ${cur === "USD" ? "$" : unit}`;
          const typeLabel = t(`optype.${r.type}`);
          out.push({
            at: r.postedAt,
            row: {
              key: `${acc.id}-${r.operationId}-${cur}`,
              dateLabel: r.officeDate,
              accountName: acc.name,
              kind: acc.kind,
              description: typeLabel === `optype.${r.type}` ? r.type : typeLabel,
              note: r.note ?? "",
              inLabel: inbound ? label : "-",
              outLabel: inbound ? "-" : label,
            },
          });
        }
      }
    }
    return out.sort((a, b) => a.at.localeCompare(b.at)).map((x) => x.row);
  }, [acc1, acc2, s1.data, s2.data, usdHidden, t, unit]);

  const diffIqd = acc1 && acc2 ? decAdd(net(acc1, "IQD"), net(acc2, "IQD")) : "0";
  const diffUsd = acc1 && acc2 ? decAdd(net(acc1, "USD"), net(acc2, "USD")) : "0";
  const reconciliation: DualReconciliation | null = acc1 && acc2 ? { iqd: { state: state(diffIqd), amountLabel: fmtAbs(diffIqd) }, usd: { state: state(diffUsd), amountLabel: fmtAbs(diffUsd) } } : null;

  const typeName = (r: CustomerAccountRow) => (r.kind === "trust" ? t("cust.trusts_plural") : t("cust.debts_plural"));
  const stateText = (v: string, u: string) => {
    const s = state(v);
    return s === "we_owe" ? `${t("cust.dual_we_owe")}: ${fmtAbs(v)} ${u}` : s === "they_owe" ? `${t("cust.dual_they_owe")}: ${fmtAbs(v)} ${u}` : t("cust.dual_balanced");
  };
  const summary = acc1 && acc2
    ? [
        { label: t("cust.dual_print_balance", { name: acc1.name, type: typeName(acc1), unit }), value: `${fmt(acc1.iqd.balance)} ${unit}` },
        ...(usdHidden ? [] : [{ label: t("cust.dual_print_balance", { name: acc1.name, type: typeName(acc1), unit: "$" }), value: `${fmt(acc1.usd.balance)} $` }]),
        { label: t("cust.dual_print_balance", { name: acc2.name, type: typeName(acc2), unit }), value: `${fmt(acc2.iqd.balance)} ${unit}` },
        ...(usdHidden ? [] : [{ label: t("cust.dual_print_balance", { name: acc2.name, type: typeName(acc2), unit: "$" }), value: `${fmt(acc2.usd.balance)} $` }]),
        { label: t("cust.dual_print_rec", { unit }), value: stateText(diffIqd, unit) },
        ...(usdHidden ? [] : [{ label: t("cust.dual_print_rec", { unit: "$" }), value: stateText(diffUsd, "$") }]),
      ]
    : [];
  const title = acc1 && acc2 ? (acc1.name !== acc2.name ? t("cust.dual_print_title_two", { a: acc1.name, b: acc2.name }) : t("cust.dual_print_title_one", { a: acc1.name })) : "";

  useEffect(() => {
    if (!printing) return;
    const done = () => setPrinting(false);
    window.addEventListener("afterprint", done, { once: true });
    const h = setTimeout(() => printDocument("professional-print-template"), 80);
    return () => {
      clearTimeout(h);
      window.removeEventListener("afterprint", done);
    };
  }, [printing]);

  // [C35] one set of props for the browser print and the server PDF (D-146); the reconciliation in words (D-148,
  // old dual summary :1400-1405): "we owe" = لكم, "they owe" = عليكم, per currency
  useEffect(() => {
    const side = (v: string) => (state(v) === "we_owe" ? ("lakum" as const) : state(v) === "they_owe" ? ("alaykum" as const) : null);
    const items = [{ lines: [{ amount: diffIqd, currency: "IQD" }], side: side(diffIqd) }, ...(usdHidden || isZeroDec(diffUsd) ? [] : [{ lines: [{ amount: diffUsd, currency: "USD" }], side: side(diffUsd) }])];
    void docs.words(items).then((w) => setWords(w.filter((x) => x !== "").join(" — ")));
  }, [diffIqd, diffUsd, usdHidden]);
  const statementProps: StatementA4Props | null =
    acc1 && acc2
      ? {
          locale,
          title,
          customerName: acc1.name === acc2.name ? acc1.name : `${acc1.name} - ${acc2.name}`,
          dateLabel: officeDate(now(), tz),
          docNumber: `${acc1.accountNumber}-${acc2.accountNumber}`,
          office: printOffice,
          ...(startDate ? { periodFrom: startDate } : {}),
          ...(endDate ? { periodTo: endDate } : {}),
          columns: [
            { header: t("date"), key: "date" },
            { header: t("cust.dual_account_type"), key: "account" },
            { header: t("cust.dual_description"), key: "description" },
            { header: t("cust.dual_note"), key: "note" },
            { header: t("cust.dual_in"), key: "incoming" },
            { header: t("cust.dual_out"), key: "outgoing" },
          ],
          rows: merged.map((m) => ({ date: m.dateLabel, account: `${m.accountName} (${m.kind === "trust" ? t("cust.trusts_plural") : t("cust.debts_plural")})`, description: m.description, note: m.note || "-", incoming: m.inLabel, outgoing: m.outLabel })),
          summary,
          wordsLabel: words || null,
        }
      : null;
  const exportPdf = () => {
    if (statementProps) void docs.downloadPdf({ template: "statement_a4", props: statementProps as unknown as Record<string, unknown>, fileName: "dual-statement", docType: "dual_statement" });
  };

  const whatsapp = () => {
    const lines = [title, ...summary.map((s) => `${s.label}: ${s.value}`)];
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(lines.join("\n"))}`, "_blank", "noopener");
    toast(t("cust.whatsapp_opened"), "success");
  };

  return (
    <>
      <DualStatementModal
        options={rows.map((r) => ({ id: r.id, name: r.name, phone: r.phone ?? "", accountNumber: r.accountNumber, kind: r.kind, iqdLabel: fmt(r.iqd.balance), usdLabel: fmt(r.usd.balance) }))}
        first={first}
        second={second}
        onFirstChange={(p) => setFirst((s) => ({ ...s, ...p }))}
        onSecondChange={(p) => setSecond((s) => ({ ...s, ...p }))}
        startDate={startDate}
        endDate={endDate}
        onDatesChange={(p) => {
          if (p.startDate !== undefined) setStartDate(p.startDate);
          if (p.endDate !== undefined) setEndDate(p.endDate);
        }}
        rows={merged}
        reconciliation={reconciliation}
        usdHidden={usdHidden}
        themeColor={themeColor}
        onWhatsApp={whatsapp}
        onPdf={exportPdf}
        onPrint={() => setPrinting(true)}
        onClose={onClose}
      />
      {printing &&
        statementProps &&
        createPortal(
          <div className={PRINT_ROOT_CLASS} data-testid="print-root">
            <StatementA4 {...statementProps} />
          </div>,
          document.body,
        )}
    </>
  );
}
